/**
 * HR Patterns — /hr/patterns
 *
 * Operational pattern-review surface, ported 1-to-1 from the Lumen
 * prototype (`prototype/manager.jsx` Patterns view). Left column lists
 * surfaced flags with a collapsible "ILMU suppressed" section below.
 * Right column shows full pattern detail + framing note + the conversation
 * action. Clicking "Draft with ILMU" reveals an inline dark-navy
 * Conversation Guide panel below the detail card.
 *
 * HR / Director / ExecDirector only — gated by HrAccessGuard.
 */

import type { CSSProperties, ReactNode } from 'react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useQueries } from '@tanstack/react-query'
import type { CachedEmployeePatterns, LeaveDetailPayload, PatternKind } from '@lms/shared-types'
import { HrAccessGuard } from '../../components/hr/HrAccessGuard'
import { Card } from '../../components/primitives/Card'
import { Avatar } from '../../components/primitives/Avatar'
import { RhythmAvatar } from '../../components/manager-team/RhythmAvatar'
import { IlmuMark } from '../../components/primitives/IlmuMark'
import { ThinkingDots } from '../../components/primitives/ThinkingDots'
import { HealthBarChip } from '../../components/primitives/HealthBarChip'
import type { HealthBarBand } from '../../api/health-bar'
import { RHYTHM_STYLE } from '../../lib/rhythm-style'
import { getCachedTeamPatterns } from '../../api/patterns'
import { MANAGER_PATTERN_COPY, currentMonthName } from '../../lib/manager-pattern-copy'
import { useSession } from '../../auth/auth-context'
import { getLeaveDetail } from '../../api/leaves'
import { fmtDate } from '../../lib/format'

// ── Per-kind UI identity ──────────────────────────────────────────────────────
//
// The score/band/contributing facts come from the backend `healthBar` on each
// row — computed from ai.pattern_catalog, the engine's source of truth (see
// domain/health_bar.py). This map only carries UI identity: the mono
// `shortCode` shown in the "Pattern type" tile and a human `label` for the
// row-summary line.

type Severity = 'low' | 'medium' | 'high'

interface PatternMeta {
  shortCode: string
  label: string
}

const PATTERN_META: Record<PatternKind, PatternMeta> = {
  frequent_sick_leave: { shortCode: 'frequent_sick', label: 'Frequent sick leave' },
  poor_planning_emergency: { shortCode: 'poor_planning', label: 'Poor planning' },
  emergency_approaching_limit: {
    shortCode: 'emergency_approaching',
    label: 'Emergency approaching limit',
  },
  unpaid_leave_taken: { shortCode: 'unpaid_taken', label: 'Unpaid leave taken' },
  frequent_half_days: { shortCode: 'frequent_half_days', label: 'Frequent half-days' },
  long_weekend_pattern: { shortCode: 'long_weekend', label: 'Long-weekend pattern' },
  no_leave_in_window: { shortCode: 'no_leave_90', label: 'No leave in 90 days' },
  high_early_utilisation: { shortCode: 'high_early_util', label: 'High early utilisation' },
}

// Manager rhythm summary line per §2.2 band (FR spec "Manager rhythm summary
// lines") — one static line per band, shown at the top of the detail panel.
const RHYTHM_MANAGER_NOTE: Record<HealthBarBand, string> = {
  low: 'Nothing flagged — no action needed from you.',
  medium:
    'One or two things worth keeping on your radar. No urgent action, but good to be aware.',
  high: 'A few patterns have built up. Worth a conversation before it becomes a bigger issue.',
}

// The rhythm filter dimension — "all" plus the three bands, newest concern
// first. Labels + colours come from RHYTHM_STYLE so the pills, row badges and
// detail summary all speak one vocabulary ("Off rhythm / Heads up / Steady lah").
type RhythmFilter = 'all' | HealthBarBand
const RHYTHM_FILTERS: ReadonlyArray<{ key: RhythmFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'high', label: RHYTHM_STYLE.high.label },
  { key: 'medium', label: RHYTHM_STYLE.medium.label },
  { key: 'low', label: RHYTHM_STYLE.low.label },
]

// ── Flat per-pattern entry combining employee + pattern + meta ────────────────

type EmployeePattern = CachedEmployeePatterns['patterns'][number]

interface PatternEntry {
  id: string
  employeeId: string
  employeeName: string
  teamName: string
  /** Annual-leave snapshot for the days-left figure + balance bar. Null when
   *  the employee has no annual balance row for the current year. */
  annualLeft: number | null
  annualTotal: number | null
  /** Live collapsed-card metrics for this pattern's code (sick_days, …), used
   *  to fill the manager data-point sentence. Empty when none apply. */
  metrics: Record<string, number>
  pattern: EmployeePattern
  meta: PatternMeta
  /** Aggregate health-bar score for this employee — computed server-side from
   *  ai.pattern_catalog point weights over their active patterns, trailing
   *  12 mo (see domain/health_bar.py). Read from the row's `healthBar`. */
  employeeScore: number
  /** §2.2 band for this employee — server-computed alongside the score. */
  employeeBand: HealthBarBand
  /** Pattern codes feeding the aggregate score (for the chip tooltip). */
  contributingPatterns: ReadonlyArray<string>
}

function buildEntries(items: ReadonlyArray<CachedEmployeePatterns>): PatternEntry[] {
  const out: PatternEntry[] = []
  for (const emp of items) {
    // Score/band/contributing are authoritative from the backend healthBar
    // (ai.pattern_catalog weights + the band constants). Default to a quiet LOW
    // when absent so a missing projection never mis-renders as red.
    const employeeScore = emp.healthBar?.score ?? 0
    const employeeBand = emp.healthBar?.band ?? 'low'
    const contributingPatterns = emp.healthBar?.contributingPatterns ?? []

    for (let idx = 0; idx < emp.patterns.length; idx += 1) {
      const pattern = emp.patterns[idx]!
      out.push({
        id: `${emp.employeeId}:${pattern.kind}:${idx}`,
        employeeId: emp.employeeId,
        employeeName:
          emp.employeeName || `Employee ${emp.employeeId.slice(0, 8)}`,
        teamName: emp.teamName,
        annualLeft: emp.annualLeft,
        annualTotal: emp.annualTotal,
        metrics: emp.patternMetrics[pattern.kind] ?? {},
        pattern,
        meta: PATTERN_META[pattern.kind],
        employeeScore,
        employeeBand,
        contributingPatterns,
      })
    }
  }
  return out
}

// Within an employee, order their flags by §2.3 severity so the sharpest
// signal leads the detail panel.
const SEVERITY_RANK: Record<Severity, number> = { high: 3, medium: 2, low: 1 }

// ── Employee row (one per unique employee — the list view shape) ─────────────

interface EmployeeRow {
  employeeId: string
  employeeName: string
  teamName: string
  /** Parent org-unit name — feeds the all/department/team scope filter. */
  departmentName: string
  /** Annual-leave snapshot for the days-left figure + balance bar. */
  annualLeft: number | null
  annualTotal: number | null
  /** Soonest-expiring batch of banked replacement days (0 / null when none). */
  replacementDays: number
  replacementExpiresAt: string | null
  /** §2.2 aggregate (Red 8+, Amber 4–7, Green 0–3). */
  employeeScore: number
  employeeBand: HealthBarBand
  contributingPatterns: ReadonlyArray<string>
  /** Every entry for this employee, sorted by §2.3 severity desc — used by
   *  the right detail panel to render each pattern in turn. */
  entries: ReadonlyArray<PatternEntry>
  /** Count of active (non-suppressed) flags. Zero == "Steady lah". */
  activeCount: number
  /** The highest-severity active flag whose signal leads the row, or null
   *  when the employee has no active flags (a steady team member). */
  representative: PatternEntry | null
}

// One row per employee — INCLUDING steady members with zero flags, so the
// board reads as a whole-team rhythm roster (per the redesign) rather than a
// flags-only list. The backend returns an item per visible employee (empty
// patterns when nothing is firing), so we map items → rows directly.
function buildRows(items: ReadonlyArray<CachedEmployeePatterns>): EmployeeRow[] {
  const entries = buildEntries(items)
  const byEmployee = new Map<string, PatternEntry[]>()
  for (const e of entries) {
    const list = byEmployee.get(e.employeeId) ?? []
    list.push(e)
    byEmployee.set(e.employeeId, list)
  }
  return items.map((emp) => {
    const sorted = [...(byEmployee.get(emp.employeeId) ?? [])].sort(
      (a, b) => SEVERITY_RANK[b.pattern.severity] - SEVERITY_RANK[a.pattern.severity],
    )
    const first = sorted[0]
    const active = sorted.filter((e) => !e.pattern.suppressed)
    return {
      employeeId: emp.employeeId,
      employeeName: emp.employeeName || `Employee ${emp.employeeId.slice(0, 8)}`,
      teamName: emp.teamName,
      departmentName: emp.departmentName,
      annualLeft: emp.annualLeft,
      annualTotal: emp.annualTotal,
      replacementDays: emp.replacementDays,
      replacementExpiresAt: emp.replacementExpiresAt,
      employeeScore: first?.employeeScore ?? 0,
      employeeBand: first?.employeeBand ?? 'low',
      contributingPatterns: first?.contributingPatterns ?? [],
      entries: sorted,
      activeCount: active.length,
      representative: active[0] ?? null,
    }
  })
}


// ── Page wrappers ─────────────────────────────────────────────────────────────
//
// Same operational board is mounted for HR and managers; the backend
// `/leaves/patterns/team` route RBAC-scopes the response (manager → direct
// reports, HOD → team, HR+ → all employees), so the UI doesn't need to gate
// data — only access. HR wraps in `HrAccessGuard` (impersonation safety);
// managers don't need that guard (they can't impersonate).

export function HrPatternsPage(): JSX.Element {
  // HR overview board — company-wide, so no team scope.
  return (
    <HrAccessGuard>
      <HrPatternsPageInner />
    </HrAccessGuard>
  )
}

export function ManagerPatternsPage(): JSX.Element {
  // "My team" board — scope to the viewer's own team. For HR this narrows the
  // company-wide default to their team; managers/HODs get their natural
  // reports/team scope server-side and the teamId is ignored.
  const { user } = useSession()
  return <HrPatternsPageInner teamId={user?.teamId} />
}

function HrPatternsPageInner({ teamId }: { teamId?: string | undefined }): JSX.Element {
  const { user } = useSession()
  const query = useQuery({
    queryKey: ['patterns-team', teamId ?? null],
    queryFn: () => getCachedTeamPatterns(teamId),
  })
  const rows = useMemo(() => {
    const items = query.data?.items ?? []
    // Most at-risk first (off-rhythm → heads-up → steady), then A–Z, so the
    // people needing attention lead the roster. Score/band come from each
    // row's server-computed healthBar (ai.pattern_catalog).
    return buildRows(items).sort(
      (a, b) =>
        b.employeeScore - a.employeeScore ||
        a.employeeName.localeCompare(b.employeeName),
    )
  }, [query.data])

  const [filter, setFilter] = useState<RhythmFilter>('all')
  const [nameQuery, setNameQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Scope filter: 'all' | 'dept:<name>' | 'team:<name>' — same shape + options
  // as the Team roster's Show dropdown. Options are derived from everyone in
  // view (which already follows the who-can-see-who rule server-side), so they
  // stay stable when a scope is selected.
  const [scope, setScope] = useState('all')

  const departments = useMemo(
    () => [...new Set(rows.map((r) => r.departmentName).filter(Boolean))].sort(),
    [rows],
  )
  const teams = useMemo(
    () => [...new Set(rows.map((r) => r.teamName).filter(Boolean))].sort(),
    [rows],
  )
  const showScope = departments.length > 1 || teams.length > 1

  // Scope narrows the roster before the band + name filters. Kept separate so
  // the band counts below reflect the current scope.
  const scopeRows = useMemo(() => {
    if (scope === 'all') return rows
    if (scope.startsWith('dept:')) {
      const d = scope.slice(5)
      return rows.filter((r) => r.departmentName === d)
    }
    const t = scope.slice(5)
    return rows.filter((r) => r.teamName === t)
  }, [rows, scope])

  const counts = useMemo(() => {
    const c: Record<RhythmFilter, number> = {
      all: scopeRows.length,
      high: 0,
      medium: 0,
      low: 0,
    }
    for (const r of scopeRows) c[r.employeeBand] += 1
    return c
  }, [scopeRows])
  const attentionCount = counts.high + counts.medium

  // The detail header follows the list header's height so their bottom borders
  // line up. Measure the list header (label + search) and feed it across.
  const leftHeaderRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState<number | undefined>(undefined)
  useLayoutEffect(() => {
    const el = leftHeaderRef.current
    if (el && el.offsetHeight !== headerHeight) setHeaderHeight(el.offsetHeight)
  })

  // Scope → rhythm-band → free-text name search, narrowing in turn.
  const bandRows = useMemo(
    () =>
      filter === 'all'
        ? scopeRows
        : scopeRows.filter((r) => r.employeeBand === filter),
    [scopeRows, filter],
  )
  const visibleRows = useMemo(() => {
    const q = nameQuery.trim().toLowerCase()
    if (!q) return bandRows
    return bandRows.filter((r) => r.employeeName.toLowerCase().includes(q))
  }, [bandRows, nameQuery])

  // The list is full-width until a row is opened — no auto-select. The detail
  // panel appears only for the explicitly-selected row.
  const selectedRow = useMemo<EmployeeRow | null>(
    () => (selectedId ? rows.find((r) => r.employeeId === selectedId) ?? null : null),
    [selectedId, rows],
  )

  // Toggle: clicking the open row again closes the panel (back to full width).
  function selectRow(row: EmployeeRow): void {
    setSelectedId((cur) => (cur === row.employeeId ? null : row.employeeId))
  }

  function changeFilter(next: RhythmFilter): void {
    setFilter(next)
    setSelectedId(null)
  }

  function changeScope(next: string): void {
    setScope(next)
    // The open row may fall outside the new scope; close the detail panel.
    setSelectedId(null)
  }

  const subtitle =
    rows.length === 0
      ? 'No team members in view yet.'
      : attentionCount === 0
        ? 'Everyone is steady. Nothing needs your attention right now.'
        : `${attentionCount} ${
            attentionCount === 1 ? 'person needs' : 'people need'
          } your attention, the rest are steady.`

  return (
    <div
      className="page"
      data-screen-label="Rhythm"
      // Pin the page to the viewport (below the 68px top bar + 24px main
      // gutter) so the roster/detail panels fill the height and scroll
      // internally — no document-level scroll. The grid below flexes into
      // whatever the header + filter row leave, so no header-height guess.
      style={{ height: 'calc(100vh - 92px)', minHeight: 460 }}
    >
      <header style={{ marginBottom: 20 }}>
        <h1
          style={{
            fontFamily: 'inherit',
            fontSize: 28,
            fontWeight: 600,
            color: 'var(--text)',
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          Rhythm{' '}
          <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>
            · {attentionCount} for your attention
          </span>
        </h1>
        <p
          style={{
            color: 'var(--text-2)',
            fontSize: 15,
            margin: '8px 0 0',
            lineHeight: 1.55,
            maxWidth: '64ch',
          }}
        >
          {subtitle}
        </p>
      </header>

      {query.isLoading ? (
        <Card padding={32} style={{ textAlign: 'center' }}>
          <ThinkingDots label="Loading patterns" />
        </Card>
      ) : query.isError ? (
        <Card padding={20}>
          <div
            role="alert"
            style={{
              color: 'var(--danger-fg)',
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger-line)',
              borderRadius: 10,
              padding: '12px 14px',
              fontSize: 13,
            }}
          >
            Could not load patterns. Please try again later.
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card padding={32} style={{ borderStyle: 'dashed', textAlign: 'center' }}>
          <div style={{ color: 'var(--text-3)', fontSize: 14 }}>
            No team members in view yet.
          </div>
        </Card>
      ) : (
        <>
          <RhythmFilterBar
            filter={filter}
            counts={counts}
            onChange={changeFilter}
          />
          <div
            style={{
              display: 'grid',
              // Full-width roster until a row is opened; two columns once the
              // detail panel is showing (prototype behaviour).
              gridTemplateColumns: selectedRow ? 'minmax(280px, 360px) 1fr' : '1fr',
              // No gap between the two panes — they sit flush.
              gap: 0,
              // Fill the space the header + filter row leave, and stretch both
              // panes to it. They scroll internally, so the page never grows a
              // document-level scrollbar.
              alignItems: 'stretch',
              flex: 1,
              minHeight: 0,
              transition: 'grid-template-columns 160ms ease',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                minHeight: 0,
                height: '100%',
              }}
              data-testid="hr-patterns-sidebar"
            >
              <SidePanel
                testId="hr-patterns-flags-surfaced"
                fill
                joinRight={!!selectedRow}
                header={
                  <div
                    ref={leftHeaderRef}
                    style={{
                      padding: '10px 14px',
                      background: 'var(--surface-2)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    <div style={sidePanelHeaderLabelInlineStyle}>
                      Team · {visibleRows.length}
                    </div>
                    {/* Scope + search share one capped-width row so they stay
                        compact + left-aligned when the list is full-width, and
                        still shrink to fit when the detail panel narrows the
                        column. */}
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        maxWidth: 520,
                      }}
                    >
                      {showScope ? (
                        <select
                          aria-label="Filter by department or team"
                          data-testid="hr-patterns-scope"
                          value={scope}
                          onChange={(e) => changeScope(e.target.value)}
                          style={{ ...headerControlStyle, flex: '1 1 180px' }}
                        >
                          <option value="all">Everyone ({rows.length})</option>
                          {departments.length > 0 ? (
                            <optgroup label="Department">
                              {departments.map((d) => (
                                <option key={d} value={`dept:${d}`}>
                                  {d}
                                </option>
                              ))}
                            </optgroup>
                          ) : null}
                          {teams.length > 0 ? (
                            <optgroup label="Team">
                              {teams.map((t) => (
                                <option key={t} value={`team:${t}`}>
                                  {t}
                                </option>
                              ))}
                            </optgroup>
                          ) : null}
                        </select>
                      ) : null}
                      <input
                        type="search"
                        aria-label="Search team members by name"
                        data-testid="hr-patterns-search"
                        value={nameQuery}
                        onChange={(e) => setNameQuery(e.target.value)}
                        placeholder="Search by name"
                        style={{ ...headerControlStyle, flex: '2 1 200px' }}
                      />
                    </div>
                  </div>
                }
              >
                {visibleRows.length === 0 ? (
                  <EmptySidebarRow
                    text={
                      nameQuery.trim()
                        ? `No team members match "${nameQuery.trim()}".`
                        : 'No one in this rhythm right now.'
                    }
                  />
                ) : (
                  visibleRows.map((row) => (
                    <EmployeeListItem
                      key={row.employeeId}
                      row={row}
                      active={selectedRow?.employeeId === row.employeeId}
                      onClick={() => selectRow(row)}
                      isSelf={row.employeeId === user?.id}
                    />
                  ))
                )}
              </SidePanel>
            </div>

            {selectedRow ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  minHeight: 0,
                  height: '100%',
                }}
              >
                {/* Detail card grows to fill the pane and scrolls its own
                    content. */}
                <PatternDetailCard
                  row={selectedRow}
                  headerHeight={headerHeight}
                />
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}

// ── Rhythm filter bar ─────────────────────────────────────────────────────────
//
// All / Off rhythm / Heads up / Steady lah pills with live counts. Colours come
// from RHYTHM_STYLE so the pills, row badges and detail summary share one
// vocabulary. Selected pill fills with the band tint; the count sits in a chip.

interface RhythmFilterBarProps {
  filter: RhythmFilter
  counts: Record<RhythmFilter, number>
  onChange: (next: RhythmFilter) => void
}

function RhythmFilterBar({
  filter,
  counts,
  onChange,
}: RhythmFilterBarProps): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Filter by rhythm"
      data-testid="hr-patterns-rhythm-filter"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}
    >
      {RHYTHM_FILTERS.map(({ key, label }) => {
        const active = filter === key
        const style = key === 'all' ? null : RHYTHM_STYLE[key]
        const accent = style ? style.ring : 'var(--text-2)'
        const bg = style ? style.chipBg : 'var(--surface-2)'
        const fg = style ? style.chipText : 'var(--text)'
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`hr-patterns-filter-${key}`}
            onClick={() => onChange(key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 14px',
              borderRadius: 999,
              border: `1.5px solid ${active ? accent : 'var(--border)'}`,
              background: active ? bg : 'var(--surface)',
              color: active ? fg : 'var(--text-2)',
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'background 120ms ease, border-color 120ms ease',
            }}
          >
            {key !== 'all' && style ? (
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: style.ring,
                }}
              />
            ) : null}
            <span>{label}</span>
            <span
              style={{
                minWidth: 18,
                textAlign: 'center',
                padding: '1px 6px',
                borderRadius: 999,
                background: active ? accent : 'var(--surface-2)',
                color: active ? 'var(--surface)' : 'var(--text-2)',
                fontSize: 11,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {counts[key]}
            </span>
          </button>
        )
      })}
    </div>
  )
}

// ── Rhythm badge (Steady lah / Heads up / Off rhythm) ─────────────────────────

function RhythmBadge({
  band,
  size = 'sm',
}: {
  band: HealthBarBand
  size?: 'sm' | 'lg'
}): JSX.Element {
  const style = RHYTHM_STYLE[band]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: size === 'lg' ? '5px 14px' : '3px 10px',
        borderRadius: 999,
        background: style.chipBg,
        color: style.chipText,
        fontSize: size === 'lg' ? 13 : 11,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: style.ring,
          display: 'inline-block',
        }}
      />
      {style.label}
    </span>
  )
}

// ── Bucket pill (Wellbeing / Performance) ─────────────────────────────────────
//
// The catalog classifies every pattern as `wellbeing` or `performance` (the two
// buckets the schema carries). The pill colours the pattern card so a manager
// can tell a care signal from a planning signal at a glance.

const BUCKET_STYLE: Record<'performance' | 'wellbeing', { color: string; label: string }> = {
  performance: { color: '#6366f1', label: 'Performance' },
  wellbeing: { color: '#f59e0b', label: 'Wellbeing' },
}

function BucketPill({ bucket }: { bucket: 'performance' | 'wellbeing' }): JSX.Element {
  const b = BUCKET_STYLE[bucket] ?? BUCKET_STYLE.wellbeing
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: b.color,
        background: `${b.color}18`,
        padding: '2px 8px',
        borderRadius: 4,
        textTransform: 'uppercase',
        letterSpacing: '.04em',
      }}
    >
      {b.label}
    </span>
  )
}

// ── Annual-leave "days left" figure + bar ─────────────────────────────────────

function daysLeftColor(left: number | null, band: HealthBarBand): string {
  if (left !== null && left <= 3) return 'var(--danger)'
  return RHYTHM_STYLE[band].ring
}

// Whole numbers render bare (14), halves keep one decimal (13.5). Keeps the
// balance figure tabular-friendly without a trailing ".0".
function formatDays(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

// ── Side panel shell ──────────────────────────────────────────────────────────

interface SidePanelProps {
  title?: string
  header?: JSX.Element
  testId?: string
  /** When set, the body scrolls internally past this height instead of
   *  growing the page. The header stays pinned above the scroll area. */
  bodyMaxHeight?: string
  /** Fill the parent's height; the body flexes to fill and scrolls internally
   *  (the header stays pinned). Takes precedence over bodyMaxHeight. */
  fill?: boolean
  /** Square off the right corners so the panel joins flush with the detail
   *  pane on its right (its right border becomes the shared divider). */
  joinRight?: boolean
  children: ReactNode
}

function SidePanel({
  title,
  header,
  testId,
  bodyMaxHeight,
  fill,
  joinRight,
  children,
}: SidePanelProps): JSX.Element {
  return (
    <div
      data-testid={testId}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        // When joined to the detail pane, the right border is the shared
        // divider — make it a touch heavier so the seam reads deliberately.
        borderRightWidth: joinRight ? 2 : 1,
        borderRadius: 12,
        borderTopRightRadius: joinRight ? 0 : 12,
        borderBottomRightRadius: joinRight ? 0 : 12,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: fill ? '100%' : undefined,
      }}
    >
      {header ? header : <div style={sidePanelHeaderLabelStyle}>{title}</div>}
      {fill ? (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</div>
      ) : bodyMaxHeight ? (
        <div style={{ overflowY: 'auto', maxHeight: bodyMaxHeight, minHeight: 0 }}>
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  )
}

function EmptySidebarRow({ text }: { text: string }): JSX.Element {
  return (
    <div
      style={{
        padding: '14px',
        fontSize: 12.5,
        color: 'var(--text-3)',
        textAlign: 'center',
      }}
    >
      {text}
    </div>
  )
}

// ── Left list row (one per employee) ────────────────────────────────────────

interface EmployeeListItemProps {
  row: EmployeeRow
  active: boolean
  onClick: () => void
  /** True when this row is the signed-in viewer — surfaces a "(You)" chip. */
  isSelf?: boolean
}

function EmployeeListItem({
  row,
  active,
  onClick,
  isSelf = false,
}: EmployeeListItemProps): JSX.Element {
  // The row speaks the rhythm vocabulary — avatar tinted by the §2.2 aggregate
  // band, a "days left" figure, and the Steady lah / Heads up / Off rhythm
  // badge. The one-liner summarises the active flags (or a steady note).
  const band = row.employeeBand
  const style = RHYTHM_STYLE[band]
  const rep = row.representative
  const summary =
    row.activeCount === 0
      ? 'No patterns flagged.'
      : row.entries
          .filter((e) => !e.pattern.suppressed)
          .map((e) => e.meta.label.toLowerCase())
          .join(', ')
  const left = row.annualLeft

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`hr-patterns-row-${row.employeeId}`}
      aria-current={active ? 'true' : undefined}
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        padding: '14px 14px',
        width: '100%',
        textAlign: 'left',
        background: active ? 'var(--baby-blue-tint)' : 'transparent',
        borderTop: '1px solid var(--border)',
        borderLeft: `3px solid ${active ? style.ring : 'transparent'}`,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      <RhythmAvatar name={row.employeeName} band={band} patternCount={row.activeCount} size={34} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: 'var(--text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.employeeName}
          </span>
          {isSelf ? (
            <span
              style={{
                flexShrink: 0,
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.02em',
                color: 'var(--steel-blue)',
                background: 'var(--baby-blue-tint)',
                border: '1px solid var(--steel-blue)',
                borderRadius: 999,
                padding: '1px 6px',
                lineHeight: 1.4,
              }}
            >
              You
            </span>
          ) : null}
        </div>
        <div
          style={{
            fontSize: 12,
            color: rep ? 'var(--text-2)' : 'var(--text-3)',
            marginTop: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {summary}
        </div>
      </div>
      {left !== null ? (
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'baseline',
            gap: 3,
            color: daysLeftColor(left, band),
          }}
        >
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatDays(left)}
          </span>
          <span style={{ fontSize: 9.5, color: 'var(--text-3)' }}>days left</span>
        </div>
      ) : null}
      <RhythmBadge band={band} />
    </button>
  )
}

// ── Right detail panel ────────────────────────────────────────────────────────

interface PatternDetailCardProps {
  /** The selected employee's whole rhythm picture — band, balance and every
   *  pattern entry (surfaced + suppressed), severity desc. */
  row: EmployeeRow
  /** Match the list header's height so the two header bottom-borders align. */
  headerHeight?: number | undefined
}

function PatternDetailCard({
  row,
  headerHeight,
}: PatternDetailCardProps): JSX.Element | null {
  const { employeeName, teamName, entries, employeeBand } = row
  const style = RHYTHM_STYLE[employeeBand]
  const allSuppressed = entries.length > 0 && row.activeCount === 0
  return (
    <Card
      padding={0}
      style={{
        overflow: 'auto',
        flex: 1,
        minHeight: 0,
        // Join flush with the list on the left: square off the left corners
        // and drop the left border so the list's right border is the single
        // divider between the two panes.
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderLeftWidth: 0,
      }}
    >
      <div
        style={{
          height: headerHeight,
          boxSizing: 'border-box',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 28px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <Avatar name={employeeName} size={36} />
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              {employeeName}
              {/* §2.2 aggregate risk band — Red/Amber for HIGH/MEDIUM; LOW
                  renders nothing so clean employees don't show green noise. */}
              <HealthBarChip
                band={row.employeeBand}
                score={row.employeeScore}
                contributingPatterns={row.contributingPatterns}
                size="sm"
                testId="hr-patterns-health-bar"
              />
            </div>
            {teamName ? (
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{teamName}</div>
            ) : null}
          </div>
        </div>
        <RhythmBadge band={employeeBand} size="lg" />
      </div>

      {/* Manager-facing rhythm summary — what, if anything, to do. */}
      <div style={{ padding: '14px 28px 0' }}>
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 10,
            background: style.fill,
            border: `1px solid ${style.ring}30`,
            fontSize: 13,
            color: 'var(--text-2)',
            lineHeight: 1.55,
          }}
        >
          {RHYTHM_MANAGER_NOTE[employeeBand]}
        </div>
      </div>

      {row.annualTotal !== null && row.annualLeft !== null ? (
        <BalanceBar
          left={row.annualLeft}
          total={row.annualTotal}
          band={employeeBand}
        />
      ) : null}

      {row.replacementDays > 0 && row.replacementExpiresAt ? (
        <ReplacementExpiryCallout
          days={row.replacementDays}
          expiresAt={row.replacementExpiresAt}
        />
      ) : null}

      {/* No empty-state callout here: for a steady member the rhythm summary
          above ("Nothing flagged, no action needed from you.") already says it,
          so a second "No patterns flagged" line would be redundant. */}
      {entries.map((entry) => (
        <PatternSection key={entry.id} entry={entry} />
      ))}

      {allSuppressed ? (
        <div style={{ padding: '16px 28px 20px' }}>
          <SuppressedCallout />
        </div>
      ) : null}
    </Card>
  )
}

// ── Annual-leave balance bar ──────────────────────────────────────────────────

function BalanceBar({
  left,
  total,
  band,
}: {
  left: number
  total: number
  band: HealthBarBand
}): JSX.Element {
  const pct = total > 0 ? Math.min((left / total) * 100, 100) : 0
  const color = daysLeftColor(left, band)
  return (
    <div
      style={{ padding: '14px 28px', borderBottom: '1px solid var(--border)' }}
      data-testid="hr-patterns-balance"
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: '.05em',
          marginBottom: 4,
        }}
      >
        Annual leave remaining
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          style={{
            fontSize: 19,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            color,
          }}
        >
          {formatDays(left)}
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
          of {formatDays(total)} days
        </span>
      </div>
      <div
        style={{
          height: 4,
          background: 'var(--surface-2)',
          borderRadius: 99,
          marginTop: 6,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: color,
            borderRadius: 99,
          }}
        />
      </div>
    </div>
  )
}

// ── Replacement-leave expiry nudge ────────────────────────────────────────────
//
// Banked replacement days are use-it-or-lose-it. Surface the soonest-expiring
// batch so a manager can prompt the employee to spend them before they lapse.

function ReplacementExpiryCallout({
  days,
  expiresAt,
}: {
  days: number
  expiresAt: string
}): JSX.Element {
  const label = days === 1 ? 'replacement day' : 'replacement days'
  return (
    <div
      style={{ padding: '10px 28px 0' }}
      data-testid="hr-patterns-replacement"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          borderRadius: 10,
          background: 'var(--warning-bg)',
          border: '1px solid var(--warning-line)',
        }}
      >
        <span aria-hidden style={{ fontSize: 15 }}>
          ⏳
        </span>
        <div style={{ lineHeight: 1.35 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--warning-fg)' }}>
            {formatDays(days)} {label} expiring
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
            Expires {fmtDate(expiresAt)}
          </div>
        </div>
      </div>
    </div>
  )
}

interface PatternSectionProps {
  entry: PatternEntry
}

function PatternSection({ entry }: PatternSectionProps): JSX.Element {
  const { pattern } = entry
  // Manager-view copy (FR spec "Manager view patterns"): a static title, the
  // dynamic third-person data-point sentence built from the SAME live metric
  // the employee sees (with the detector evidence count as fallback), and a
  // static manager-directed nudge. This is deliberately NOT the employee/home
  // copy — the nudge speaks to the manager's action.
  const copy = MANAGER_PATTERN_COPY[pattern.kind]
  const title = copy.title
  const signal = copy.dataPoint(entry.metrics, pattern.evidence.count, currentMonthName())
  const advice = copy.nudge
  // Dot + tint are keyed to the bucket so a care signal (wellbeing) and a
  // planning signal (performance) read apart at a glance.
  const dotColor = BUCKET_STYLE[pattern.bucket]?.color ?? BUCKET_STYLE.wellbeing.color
  return (
    <div
      data-testid={`hr-patterns-section-${entry.id}`}
      style={{
        // No rule between cards — the tinted pattern card + the padding gap
        // already separate them; a divider read as clutter.
        padding: '10px 28px',
        opacity: pattern.suppressed ? 0.75 : 1,
      }}
    >
      <div
        style={{
          borderRadius: 10,
          border: `1px solid ${dotColor}30`,
          background: `${dotColor}08`,
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '11px 14px 9px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: dotColor,
              flexShrink: 0,
              marginTop: 5,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                {title}
              </span>
              <BucketPill bucket={pattern.bucket} />
              {pattern.performanceRisk ? (
                <span style={performanceRiskBadgeStyle}>Perf flag</span>
              ) : null}
              {pattern.suppressed ? (
                <span style={suppressedBadgeStyle}>ILMU suppressed</span>
              ) : null}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>
              {signal}
            </div>
          </div>
        </div>
        {advice ? (
          <div
            data-testid="hr-patterns-detail-detail"
            style={{
              padding: '9px 14px 11px 32px',
              borderTop: `1px solid ${dotColor}20`,
              background: `${dotColor}0a`,
              fontSize: 12,
              color: 'var(--text-2)',
              lineHeight: 1.6,
            }}
          >
            <span style={{ color: dotColor }}>💡 </span>
            {advice}
          </div>
        ) : null}
      </div>

      <EvidenceSection leaveIds={pattern.evidence.sampleLeaveIds} />
    </div>
  )
}

// ── Evidence / Occurrences ────────────────────────────────────────────────────
//
// Resolves the flag's `evidence.sampleLeaveIds` (the actual leave requests the
// detector counted) to dated rows. Fetches run in parallel and are cached per
// leave id, so re-selecting a flag is free. Renders nothing when the pattern
// has no sampled leaves (e.g. no_leave_in_window, which fires on the *absence*
// of leave).

interface EvidenceSectionProps {
  leaveIds: ReadonlyArray<string>
}

function EvidenceSection({ leaveIds }: EvidenceSectionProps): JSX.Element | null {
  const navigate = useNavigate()
  const results = useQueries({
    queries: leaveIds.map((id) => ({
      queryKey: ['leave-detail', id],
      queryFn: () => getLeaveDetail(id),
      staleTime: 5 * 60_000,
    })),
  })

  if (leaveIds.length === 0) return null

  const anyLoading = results.some((r) => r.isLoading)
  const leaves = results
    .map((r) => r.data)
    .filter((d): d is NonNullable<typeof d> => Boolean(d))
    // Newest first.
    .sort((a, b) => (a.start_date < b.start_date ? 1 : -1))

  return (
    <div
      data-testid="hr-patterns-occurrences"
      style={{ marginTop: 14, background: 'var(--surface-2)', padding: 12, borderRadius: 10 }}
    >
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          color: 'var(--text-3)',
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        Occurrences
      </div>
      {anyLoading && leaves.length === 0 ? (
        <ThinkingDots label="Loading supporting leaves" />
      ) : leaves.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          Supporting leaves are no longer accessible.
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {leaves.map((lv) => (
            <OccurrenceChip
              key={lv.id}
              leave={lv}
              onOpen={(id) => navigate({ to: '/leave', search: { id } as never })}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// A single occurrence chip. Clickable — opens the underlying leave request in
// the full leave detail view (`/leave?id=…`), the same surface managers reach
// from their queue. Native <button> for keyboard access; hover lifts the chip
// so the affordance is obvious.
function OccurrenceChip({
  leave,
  onOpen,
}: {
  leave: LeaveDetailPayload
  onOpen: (id: string) => void
}): JSX.Element {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      onClick={() => onOpen(leave.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={`Open leave detail — ${fmtDate(leave.start_date)} · ${leave.type}`}
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        padding: '3px 8px',
        borderRadius: 999,
        border: '1px solid var(--border)',
        background: hover ? 'var(--baby-blue-tint)' : 'var(--surface)',
        color: hover ? 'var(--text)' : 'var(--text-2)',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        transition: 'background 120ms ease, color 120ms ease',
      }}
    >
      {fmtDate(leave.start_date)}
      {leave.half_day ? ' · half-day' : ''} · {leave.type} · {leave.days}d
    </button>
  )
}

function FramingNote({ text }: { text: string }): JSX.Element {
  return (
    <div
      style={{
        marginTop: 14,
        padding: 12,
        border: '1px solid var(--border)',
        borderRadius: 10,
        background: 'var(--pear-tint-2)',
        display: 'flex',
        gap: 10,
      }}
    >
      <IlmuMark size={16} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text)', lineHeight: 1.55 }}>
          {text}
        </div>
      </div>
    </div>
  )
}

function SuppressedCallout(): JSX.Element {
  return (
    <div
      data-testid="hr-patterns-suppressed-callout"
      style={{
        marginTop: 14,
        padding: 14,
        border: '1px dashed var(--border)',
        borderRadius: 12,
        background: 'var(--surface-2)',
      }}
    >
      <div
        style={{
          fontWeight: 600,
          marginBottom: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <InfoIcon />
        ILMU is not asking you to act on this.
      </div>
      <div style={{ color: 'var(--text-2)', fontSize: 12.5, lineHeight: 1.5 }}>
        Visible here for review only. If you disagree with the suppression,
        open it for action — but most managers don't.
      </div>
    </div>
  )
}

// ── Inline icons ──────────────────────────────────────────────────────────────

function ChevronIcon({ expanded }: { expanded: boolean }): JSX.Element {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      style={{
        transition: 'transform 150ms ease',
        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
        flexShrink: 0,
      }}
    >
      <path
        d="M3 5l4 4 4-4"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}


function InfoIcon(): JSX.Element {
  return (
    <svg width={14} height={14} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="6" stroke="var(--text-2)" strokeWidth={1.3} />
      <path
        d="M7 6v4"
        stroke="var(--text-2)"
        strokeWidth={1.3}
        strokeLinecap="round"
      />
      <circle cx="7" cy="4" r="0.7" fill="var(--text-2)" />
    </svg>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

const sidePanelHeaderLabelStyle: CSSProperties = {
  padding: '10px 14px',
  background: 'var(--surface-2)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  color: 'var(--text-2)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}

const sidePanelHeaderLabelInlineStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  color: 'var(--text-2)',
  flex: 1,
}

// Shared style for the Rhythm list-header controls (scope select + name
// search). Width is driven by the flex row that wraps them, not width:100%,
// so they stay compact + left-aligned when the list is full-width.
const headerControlStyle: CSSProperties = {
  minWidth: 0,
  padding: '7px 10px',
  fontSize: 13,
  border: '1px solid var(--border)',
  borderRadius: 8,
  background: 'var(--surface)',
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
}

const sidePanelHeaderButtonStyle: CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  background: 'var(--surface-2)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  textAlign: 'left',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const ilmuButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  background: '#0F1B2E',
  color: 'rgba(255,255,255,0.95)',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'inherit',
}

const ghostButtonStyle: CSSProperties = {
  fontSize: 12,
  padding: '6px 12px',
  background: 'rgba(255,255,255,.08)',
  color: 'rgba(255,255,255,0.95)',
  border: '1px solid rgba(255,255,255,.12)',
  borderRadius: 8,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const sentChipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 12px',
  background: 'rgba(205, 222, 0, 0.12)',
  color: 'var(--pear)',
  border: '1px solid rgba(205, 222, 0, 0.32)',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 500,
}

const wellnessBadgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  background: 'var(--baby-blue-tint)',
  color: '#0F4F7A',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 500,
}

const performanceRiskBadgeStyle: CSSProperties = {
  ...wellnessBadgeStyle,
  background: 'var(--danger-bg)',
  color: 'var(--danger-fg)',
}

const suppressedBadgeStyle: CSSProperties = {
  ...wellnessBadgeStyle,
  background: 'var(--surface-2)',
  color: 'var(--text-3)',
  border: '1px dashed var(--border)',
}

const guideListStyle: CSSProperties = {
  margin: 0,
  paddingLeft: 16,
  color: 'rgba(255,255,255,.92)',
}

const guideListMutedStyle: CSSProperties = {
  margin: 0,
  paddingLeft: 16,
  color: 'rgba(255,255,255,.85)',
  fontSize: 12.5,
}
